import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getOrganisations, createOrganisation, updateOrganisation,
  getUsers, createUser, updateUser, toggleUserActive, resetUserPassword,
  getCustomers, createCustomer, updateCustomer, toggleCustomerActive
} from '../services/api';
import { formatDate, StatusBadge } from '../utils/helpers';
import {
  Building2, Users, UserCheck, Plus, Edit2, Power,
  Key, ChevronDown, ChevronUp, X, Check, Search
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AppModal, ConfirmModal } from '../components/common/AppModal';

// ─── Shared helpers ───────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <AppModal title={title} onClose={onClose} maxWidth={672}>
      {children}
    </AppModal>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

const ROLES = ['ADMIN', 'SELLER', 'FINANCE'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];
const APPROVAL_MODES = [
  { value: 'AUTO_APPROVE',     label: 'Auto Approve' },
  { value: 'DRAFT_FOR_REVIEW', label: 'Draft for Review' },
  { value: 'DRAFT_WITH_SLA',   label: 'Draft with SLA' },
];

// ═══════════════════════════════════════════════════════════════
// TAB 1 — ORGANISATION
// ═══════════════════════════════════════════════════════════════
function OrganisationTab() {
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | 'create' | org object
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getOrganisations().then(r => setOrgs(r.data.organisations || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ type: 'SELLER', currency: 'INR', invoice_approval_mode: 'DRAFT_FOR_REVIEW',
               approval_sla_hours: 24, fiscal_year_start: 4, country: 'India' });
    setModal('create');
  };
  const openEdit = (org) => { setForm({ ...org }); setModal(org); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'create') {
        await createOrganisation(form);
        toast.success('Organisation created');
      } else {
        await updateOrganisation(modal.org_id, form);
        toast.success('Organisation updated');
      }
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Your seller organisation(s) and their settings.</p>
        <button onClick={openCreate} className="btn-primary"><Plus size={15}/> Add Organisation</button>
      </div>

      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-xl"/> :
        orgs.map(org => (
          <div key={org.org_id} className="card p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Building2 size={18} className="text-primary-600"/>
                  <h3 className="font-semibold text-lg">{org.name}</h3>
                  <span className="badge bg-primary-100 text-primary-700">{org.type}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                  {[
                    { l:'GSTIN',    v: org.gstin },
                    { l:'PAN',      v: org.pan },
                    { l:'Currency', v: org.currency },
                    { l:'Invoice Approval', v: org.invoice_approval_mode?.replace(/_/g,' ') },
                    { l:'Email',    v: org.email },
                    { l:'Phone',    v: org.phone },
                    { l:'City',     v: org.city },
                    { l:'Users',    v: org.user_count || 0 },
                  ].map(f => (
                    <div key={f.l}>
                      <div className="text-xs text-gray-400">{f.l}</div>
                      <div className="font-medium text-gray-800">{f.v || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => openEdit(org)} className="btn-secondary text-sm">
                <Edit2 size={13}/> Edit
              </button>
            </div>
          </div>
        ))
      }

      {modal !== null && (
        <Modal title={modal === 'create' ? 'Add Organisation' : `Edit: ${modal.name}`} onClose={() => setModal(null)}>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Organisation Name" required>
                <input className="input" value={form.name||''} onChange={e=>set('name',e.target.value)} required/>
              </Field>
              <Field label="Type">
                <select className="select" value={form.type||'SELLER'} onChange={e=>set('type',e.target.value)}>
                  <option value="SELLER">Seller</option>
                  <option value="BUYER">Buyer</option>
                  <option value="BOTH">Both</option>
                </select>
              </Field>
              <Field label="GSTIN"><input className="input" value={form.gstin||''} onChange={e=>set('gstin',e.target.value)} placeholder="22AAAAA0000A1Z5"/></Field>
              <Field label="PAN"><input className="input" value={form.pan||''} onChange={e=>set('pan',e.target.value)} placeholder="AAAAA9999A"/></Field>
              <Field label="Email"><input type="email" className="input" value={form.email||''} onChange={e=>set('email',e.target.value)}/></Field>
              <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>set('phone',e.target.value)}/></Field>
              <Field label="Currency">
                <select className="select" value={form.currency||'INR'} onChange={e=>set('currency',e.target.value)}>
                  {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Invoice Approval Mode">
                <select className="select" value={form.invoice_approval_mode||'DRAFT_FOR_REVIEW'} onChange={e=>set('invoice_approval_mode',e.target.value)}>
                  {APPROVAL_MODES.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="City"><input className="input" value={form.city||''} onChange={e=>set('city',e.target.value)}/></Field>
              <Field label="State"><input className="input" value={form.state||''} onChange={e=>set('state',e.target.value)}/></Field>
              <Field label="Pincode"><input className="input" value={form.pincode||''} onChange={e=>set('pincode',e.target.value)}/></Field>
              <Field label="Country"><input className="input" value={form.country||'India'} onChange={e=>set('country',e.target.value)}/></Field>
            </div>
            <Field label="Address">
              <textarea className="input" rows={2} value={form.address||''} onChange={e=>set('address',e.target.value)}/>
            </Field>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? 'Saving…' : modal === 'create' ? 'Create Organisation' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 2 — USERS (Platform users — Seller staff)
// ═══════════════════════════════════════════════════════════════
function UsersTab() {
  const [users, setUsers]       = useState([]);
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | 'create' | user obj
  const [pwdModal, setPwdModal] = useState(null); // user obj
  const [form, setForm]         = useState({});
  const [newPwd, setNewPwd]     = useState('');
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getUsers(), getOrganisations()])
      .then(([u, o]) => { setUsers(u.data.users || []); setOrgs(o.data.organisations || []); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => {
    setForm({ role: 'SELLER', org_id: orgs[0]?.org_id || '' });
    setModal('create');
  };
  const openEdit = (u) => { setForm({ ...u, password: '' }); setModal(u); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === 'create') {
        await createUser(form);
        toast.success('User created successfully');
      } else {
        await updateUser(modal.user_id, form);
        toast.success('User updated');
      }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    try {
      await toggleUserActive(u.user_id);
      toast.success(u.is_active ? 'User deactivated' : 'User activated');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleResetPwd = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await resetUserPassword(pwdModal.user_id, { new_password: newPwd });
      toast.success('Password reset successfully');
      setPwdModal(null); setNewPwd('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const filtered = users.filter(u =>
    !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const roleColor = { ADMIN:'bg-purple-100 text-purple-700', SELLER:'bg-blue-100 text-blue-700', FINANCE:'bg-green-100 text-green-700' };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="relative w-56 shrink-0">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"/>
          <input className="input input-with-icon w-full" placeholder="Search users…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <button onClick={openCreate} className="btn-primary"><Plus size={15}/> Add User</button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr>{['Name','Email','Role','Organisation','Designation','Status','Actions'].map(h=>
              <th key={h} className="table-th">{h}</th>
            )}</tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="table-td text-center text-gray-400">Loading…</td></tr>
            : filtered.length === 0 ? <tr><td colSpan={7} className="table-td text-center text-gray-400">No users found</td></tr>
            : filtered.map(u => (
              <tr key={u.user_id} className="hover:bg-gray-50">
                <td className="table-td font-medium">{u.name}</td>
                <td className="table-td text-gray-500 text-xs">{u.email}</td>
                <td className="table-td">
                  <span className={`badge ${roleColor[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role}</span>
                </td>
                <td className="table-td text-gray-500 text-sm">{u.organisation_name || '—'}</td>
                <td className="table-td text-gray-500 text-sm">{u.designation || '—'}</td>
                <td className="table-td">
                  <span className={`badge ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="table-td">
                  <div className="flex items-center gap-1.5">
                    <button onClick={()=>openEdit(u)} title="Edit" className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                      <Edit2 size={13}/>
                    </button>
                    <button onClick={()=>handleToggle(u)} title={u.is_active?'Deactivate':'Activate'}
                      className={`p-1.5 rounded hover:bg-gray-100 ${u.is_active?'text-red-400':'text-green-500'}`}>
                      <Power size={13}/>
                    </button>
                    <button onClick={()=>setPwdModal(u)} title="Reset password" className="p-1.5 rounded hover:bg-gray-100 text-gray-400">
                      <Key size={13}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {modal !== null && (
        <Modal title={modal === 'create' ? 'Add Platform User' : `Edit User: ${modal.name}`} onClose={()=>setModal(null)}>
          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
            Platform users are <strong>seller-side staff</strong> who manage POs, invoices and revenue on this platform.
            Buyers/customers are onboarded separately under the <strong>Customers</strong> tab.
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <input className="input" value={form.name||''} onChange={e=>set('name',e.target.value)} required/>
              </Field>
              <Field label="Email Address" required>
                <input type="email" className="input" value={form.email||''}
                  onChange={e=>set('email',e.target.value)} required disabled={modal !== 'create'}
                  className={`input ${modal !== 'create' ? 'bg-gray-50 text-gray-400' : ''}`}/>
              </Field>
              {modal === 'create' && (
                <Field label="Password" required>
                  <input type="password" className="input" value={form.password||''}
                    onChange={e=>set('password',e.target.value)} required minLength={8}
                    placeholder="Minimum 8 characters"/>
                </Field>
              )}
              <Field label="Role" required>
                <select className="select" value={form.role||'SELLER'} onChange={e=>set('role',e.target.value)}>
                  {ROLES.map(r=><option key={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Organisation">
                <select className="select" value={form.org_id||''} onChange={e=>set('org_id',e.target.value)}>
                  <option value="">— Select organisation —</option>
                  {orgs.map(o=><option key={o.org_id} value={o.org_id}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Designation">
                <input className="input" value={form.designation||''} onChange={e=>set('designation',e.target.value)}
                  placeholder="e.g. Finance Manager"/>
              </Field>
              <Field label="Phone">
                <input className="input" value={form.phone||''} onChange={e=>set('phone',e.target.value)}/>
              </Field>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500 space-y-0.5">
              <p><strong>ADMIN</strong> — full access: manage users, customers, organisations</p>
              <p><strong>SELLER</strong> — manage POs, invoices, payment terms, milestones</p>
              <p><strong>FINANCE</strong> — read/approve invoices, revenue recognition, reports</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? 'Saving…' : modal === 'create' ? 'Create User' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {pwdModal && (
        <Modal title={`Reset Password — ${pwdModal.name}`} onClose={()=>{setPwdModal(null);setNewPwd('');}}>
          <form onSubmit={handleResetPwd} className="space-y-4">
            <Field label="New Password" required>
              <input type="password" className="input" value={newPwd} onChange={e=>setNewPwd(e.target.value)}
                required minLength={8} placeholder="Minimum 8 characters" autoFocus/>
            </Field>
            <div className="flex gap-3">
              <button type="button" onClick={()=>{setPwdModal(null);setNewPwd('');}} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB 3 — CUSTOMERS (Buyers — not platform users)
// ═══════════════════════════════════════════════════════════════
function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState('');
  const [expanded, setExpanded]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getCustomers(), getOrganisations()])
      .then(([c, o]) => { setCustomers(c.data.customers || []); setOrgs(o.data.organisations || []); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => {
    setForm({ seller_org_id: orgs[0]?.org_id || '', currency: 'INR',
               payment_terms_days: 30, country: 'India' });
    setModal('create');
  };
  const openEdit = (c) => { setForm({ ...c }); setModal(c); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === 'create') {
        await createCustomer(form);
        toast.success('Customer onboarded successfully');
      } else {
        await updateCustomer(modal.customer_id, form);
        toast.success('Customer updated');
      }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (c) => {
    try {
      await toggleCustomerActive(c.customer_id);
      toast.success(c.is_active ? 'Customer deactivated' : 'Customer activated');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const filtered = customers.filter(c =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.gstin || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="relative w-56 shrink-0">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"/>
          <input className="input input-with-icon w-full" placeholder="Search customers…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <button onClick={openCreate} className="btn-primary"><Plus size={15}/> Onboard Customer</button>
      </div>

      <div className="mb-3 p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700">
        <strong>Customers</strong> are your buyers — they do not have platform logins.
        They are referenced on POs and invoices. All communication and approvals are managed by your team on their behalf.
      </div>

      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-xl"/> :
        filtered.length === 0 ? (
          <div className="card p-12 text-center text-gray-400">
            <UserCheck size={36} className="mx-auto mb-2 opacity-30"/>
            <p>No customers onboarded yet</p>
            <p className="text-xs mt-1">Click "Onboard Customer" to add your first buyer</p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>{['Customer','Contact','GSTIN','Seller Org','POs','Status','Actions'].map(h=>
                  <th key={h} className="table-th">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.customer_id} className="hover:bg-gray-50">
                    <td className="table-td">
                      <div className="font-medium">{c.name}</div>
                      {c.display_name && c.display_name !== c.name &&
                        <div className="text-xs text-gray-400">{c.display_name}</div>}
                    </td>
                    <td className="table-td text-xs text-gray-500">
                      <div>{c.contact_person || c.email || '—'}</div>
                      <div>{c.contact_phone || c.phone || ''}</div>
                    </td>
                    <td className="table-td font-mono text-xs">{c.gstin || '—'}</td>
                    <td className="table-td text-sm text-gray-500">{c.seller_org_name || '—'}</td>
                    <td className="table-td text-center font-medium">{c.po_count || 0}</td>
                    <td className="table-td">
                      <span className={`badge ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="table-td">
                      <div className="flex gap-1.5">
                        <button onClick={()=>openEdit(c)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Edit2 size={13}/></button>
                        <button onClick={()=>handleToggle(c)} className={`p-1.5 rounded hover:bg-gray-100 ${c.is_active?'text-red-400':'text-green-500'}`}><Power size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {modal !== null && (
        <Modal title={modal === 'create' ? 'Onboard Customer' : `Edit Customer: ${modal.name}`} onClose={()=>setModal(null)}>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Customer / Company Name" required>
                <input className="input" value={form.name||''} onChange={e=>set('name',e.target.value)} required/>
              </Field>
              <Field label="Display Name">
                <input className="input" value={form.display_name||''} onChange={e=>set('display_name',e.target.value)} placeholder="Short name for display"/>
              </Field>
              <Field label="Seller Organisation" required>
                <select className="select" value={form.seller_org_id||''} onChange={e=>set('seller_org_id',e.target.value)} required>
                  <option value="">— Select your organisation —</option>
                  {orgs.map(o=><option key={o.org_id} value={o.org_id}>{o.name}</option>)}
                </select>
              </Field>
              <Field label="Currency">
                <select className="select" value={form.currency||'INR'} onChange={e=>set('currency',e.target.value)}>
                  {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="GSTIN"><input className="input" value={form.gstin||''} onChange={e=>set('gstin',e.target.value)} placeholder="22AAAAA0000A1Z5"/></Field>
              <Field label="PAN"><input className="input" value={form.pan||''} onChange={e=>set('pan',e.target.value)} placeholder="AAAAA9999A"/></Field>
              <Field label="Email"><input type="email" className="input" value={form.email||''} onChange={e=>set('email',e.target.value)}/></Field>
              <Field label="Phone"><input className="input" value={form.phone||''} onChange={e=>set('phone',e.target.value)}/></Field>
              <Field label="Contact Person"><input className="input" value={form.contact_person||''} onChange={e=>set('contact_person',e.target.value)}/></Field>
              <Field label="Contact Email"><input type="email" className="input" value={form.contact_email||''} onChange={e=>set('contact_email',e.target.value)}/></Field>
              <Field label="Contact Phone"><input className="input" value={form.contact_phone||''} onChange={e=>set('contact_phone',e.target.value)}/></Field>
              <Field label="Payment Terms (days)">
                <input type="number" className="input" value={form.payment_terms_days||30} onChange={e=>set('payment_terms_days',parseInt(e.target.value)||30)}/>
              </Field>
              <Field label="Credit Limit">
                <input type="number" step="0.01" className="input" value={form.credit_limit||''} onChange={e=>set('credit_limit',e.target.value)} placeholder="Leave blank for unlimited"/>
              </Field>
              <Field label="City"><input className="input" value={form.city||''} onChange={e=>set('city',e.target.value)}/></Field>
              <Field label="State"><input className="input" value={form.state||''} onChange={e=>set('state',e.target.value)}/></Field>
              <Field label="Pincode"><input className="input" value={form.pincode||''} onChange={e=>set('pincode',e.target.value)}/></Field>
              <Field label="Country"><input className="input" value={form.country||'India'} onChange={e=>set('country',e.target.value)}/></Field>
            </div>
            <Field label="Address"><textarea className="input" rows={2} value={form.address||''} onChange={e=>set('address',e.target.value)}/></Field>
            <Field label="Notes"><textarea className="input" rows={2} value={form.notes||''} onChange={e=>set('notes',e.target.value)}/></Field>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={()=>setModal(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? 'Saving…' : modal === 'create' ? 'Onboard Customer' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════
export default function AdminSettings() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('organisations');

  const tabs = [
    { id:'organisations', label:'Organisations', icon:Building2, desc:'Manage your seller organisations' },
    { id:'users',         label:'Platform Users', icon:Users,     desc:'Staff who use this platform' },
    { id:'customers',     label:'Customers',      icon:UserCheck, desc:'Your buyers / clients' },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Settings</h2>
        <p className="text-sm text-gray-500 mt-0.5">Manage your organisations, platform users, and customer directory.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${activeTab === tab.id
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'}`}>
            <tab.icon size={15}/>{tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {!isAdmin && (
        <div className="card p-8 text-center text-gray-400">
          <p>Only Admins can manage settings.</p>
        </div>
      )}
      {isAdmin && activeTab === 'organisations' && <OrganisationTab />}
      {isAdmin && activeTab === 'users'         && <UsersTab />}
      {isAdmin && activeTab === 'customers'     && <CustomersTab />}
    </div>
  );
}
